import React from 'react'
import Box from 'components/atoms/AppBox/AppBox'
import AppInput from 'components/atoms/AppInput/AppInput'
import AppButton from 'components/atoms/AppButton/AppButton'
import IconAdd from '../../../../../assets/plus.png'
import { useNavigate } from 'react-router-dom'
import { OperationMode } from 'core/domain/operationMode'
import { OperationModeBadge } from 'core/presentation/components/molecules/OperationModeBadge/OperationModeBadge'

interface Props {
    children: React.ReactNode
    templateName?: string
    buttonDisabled?: boolean
    onAddRule?: () => void
    onNameChange?: (name: string) => void
    onCreateTemplate?: () => void
    onEditTemplate?: () => void
    operationModes?: OperationMode[]
    selectedOperationMode?: string
    onOperationModeChange?: (code: string, id: number) => void
    currentOperationMode?: OperationMode
    nameError?: string
    operationModeError?: string
    rulesError?: string
    hideFooterActions?: boolean
    readOnlyRules?: boolean
    nameReadOnly?: boolean
}

const FieldError = ({ message }: { message?: string }) => {
    if (message === undefined || message === '') return null

    return (
        <p role="alert" className="text-sm font-normal text-[#CE1821] mt-1">
            {message}
        </p>
    )
}

const PatternForm = ({
    children,
    onAddRule,
    onCreateTemplate,
    onNameChange,
    templateName,
    onEditTemplate,
    buttonDisabled,
    operationModes = [],
    selectedOperationMode = '',
    onOperationModeChange,
    currentOperationMode,
    nameError,
    operationModeError,
    rulesError,
    hideFooterActions = false,
    readOnlyRules = false,
    nameReadOnly = false,
}: Props) => {
    const navigate = useNavigate()

    return (
        <Box>
            <div>
                <h2 className="font-semibold mb-4 text-xl text-[#070A13] leading-[24px]">
                    Nombre de la Plantilla
                </h2>

                {/* Input + segmented operation mode toggle in same row */}
                <div className="flex items-center gap-4">
                    <AppInput
                        placeholder="Nombre de la plantilla"
                        value={templateName ?? ''}
                        onChange={(e) => onNameChange?.(e.target.value)}
                        disabled={nameReadOnly}
                    />

                    {onEditTemplate ? (
                        currentOperationMode
                            ? <OperationModeBadge operationMode={currentOperationMode} />
                            : <span className="text-sm text-[#475569]">—</span>
                    ) : (
                        /* Segmented toggle */
                        <div className="flex flex-shrink-0 rounded-xl overflow-hidden border border-[#E2E8F0] bg-[#F0F4F8]">
                            {operationModes.map((mode) => {
                                const isChecked = selectedOperationMode === mode.code
                                return (
                                    <button
                                        key={mode.id}
                                        type="button"
                                        onClick={() => onOperationModeChange?.(mode.code, mode.id)}
                                        className={`px-8 py-2.5 text-sm font-medium capitalize transition-all duration-150 focus:outline-none
                                            ${isChecked
                                                ? 'bg-white text-[#3E5060] font-semibold shadow-sm rounded-xl border border-[#E2E8F0]'
                                                : 'bg-transparent text-[#3E5060]'
                                            }`}
                                    >
                                        {mode.name}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </div>

                <FieldError message={nameError} />
                <FieldError message={operationModeError} />

                <div>
                    <h2 className="font-semibold mt-8 text-xl text-[#070A13] leading-[24px]">
                        Reglas
                    </h2>
                    <div className="mt-4 bg-[#F0F4F8] rounded-xl p-4 overflow-y-auto pr-2">
                        {children}
                        <AppButton
                            title="Agregar"
                            onClick={onAddRule}
                            disabled={readOnlyRules}
                            state="ghost"
                            size="md"
                            className="!bg-transparent font-normal !text-[#475569]"
                            icon={<img src={IconAdd} alt="add" className="w-4 h-4" />}
                        />
                    </div>
                    <FieldError message={rulesError} />
                </div>
            </div>
            {!hideFooterActions && (
                <>
                    <hr className="mt-[50px]" />
                    <div className="flex justify-end gap-3 mt-6">
                        <AppButton
                            title="Descartar"
                            onClick={() => navigate('/templates')}
                            state="ghost"
                            size="md"
                            className="font-bold"
                        />
                        <AppButton
                            title={onEditTemplate ? 'Guardar Cambios' : 'Crear Plantilla'}
                            onClick={onEditTemplate ? onEditTemplate : onCreateTemplate}
                            disabled={buttonDisabled}
                            state="primary"
                            className="bg-[#00B894] text-white font-semibold"
                        />
                    </div>
                </>
            )}
        </Box>
    )
}

PatternForm.displayName = 'PatternForm'

export default PatternForm
